/**
 * How a Next Best Action reads as a priority card.
 *
 * The feed's own `title` is a sentence built for a list ("Outstanding
 * receivable: 345435"), where `345435` is the FINANCIAL LINE'S NAME — the
 * backend builds it as `display_name(item.name, "Unnamed receivable")`, not
 * an amount and not an identifier. Printed as a card headline it reads as a
 * reference number and tells the user nothing about what to do.
 *
 * So the card is composed from the item's `kind` instead: a fixed issue
 * title, a record-appropriate action, and the server's own `title`/
 * `rationale` demoted to the context lines where the record name belongs.
 *
 * The seven kinds are `ezbuild_back`'s own producers
 * (`apps/dashboard/services/nba/producers/`). An unknown kind falls back to
 * the server's sentence rather than being dropped — a new producer must still
 * show up on Home.
 */

export type NbaPresentation = {
  /** The issue, not the record: "Payment overdue". */
  titleKey: string
  title: string
  /** A record-appropriate verb: "Review invoice", never a bare "Review". */
  actionKey: string
  action: string
  /** Which glyph family the card leads with. */
  glyph: "money" | "schedule" | "lead" | "compliance" | "budget"
}

const BY_KIND: Record<string, NbaPresentation> = {
  outstanding_receivable: {
    titleKey: "home.nba.outstandingReceivable.title",
    title: "Payment overdue",
    actionKey: "home.nba.outstandingReceivable.action",
    action: "Review invoice",
    glyph: "money"
  },
  overdue_milestone: {
    titleKey: "home.nba.overdueMilestone.title",
    title: "Milestone overdue",
    actionKey: "home.nba.overdueMilestone.action",
    action: "Review milestone",
    glyph: "schedule"
  },
  stale_lead: {
    titleKey: "home.nba.staleLead.title",
    title: "Lead follow-up",
    actionKey: "home.nba.staleLead.action",
    action: "Open lead",
    glyph: "lead"
  },
  unscheduled_follow_up: {
    titleKey: "home.nba.unscheduledFollowUp.title",
    title: "Follow-up not scheduled",
    actionKey: "home.nba.unscheduledFollowUp.action",
    action: "Open lead",
    glyph: "lead"
  },
  unsent_proposal: {
    titleKey: "home.nba.unsentProposal.title",
    title: "Proposal not sent",
    actionKey: "home.nba.unsentProposal.action",
    action: "Review proposal",
    glyph: "lead"
  },
  budget_overrun: {
    titleKey: "home.nba.budgetOverrun.title",
    title: "Budget overrun",
    actionKey: "home.nba.budgetOverrun.action",
    action: "Review budget",
    glyph: "budget"
  },
  subcontractor_compliance: {
    titleKey: "home.nba.subcontractorCompliance.title",
    title: "Compliance documents due",
    actionKey: "home.nba.subcontractorCompliance.action",
    action: "Review contractor",
    glyph: "compliance"
  }
}

export const presentationFor = (kind: string): NbaPresentation | null =>
  BY_KIND[kind] ?? null

/**
 * The record line under the title.
 *
 * The server's `title` carries the record's own name after a colon
 * ("Outstanding receivable: Kitchen deposit"). Taking the part after the
 * first colon recovers that name; a title with no colon is already just a
 * name and is used whole.
 */
export const recordNameFrom = (title: string): string => {
  const colon = title.indexOf(":")
  if (colon === -1) return title.trim()
  const after = title.slice(colon + 1).trim()
  return after || title.trim()
}
